import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, ToggleButton, ToggleButtonGroup,
  IconButton, Tooltip, Alert, Button, Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DownloadIcon from '@mui/icons-material/Download';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import InvDataGrid from '../../components/InvDataGrid';
import ModuleBadge from '../../components/ModuleBadge';
import InvButton from '../../components/InvButton';
import FilterToolbar from '../../components/FilterToolbar';
import ConfirmDialog from '../../components/ConfirmDialog';
import ControlledDrugBadge from '../../components/ControlledDrugBadge';
import { getItems, updateItem, getCategories, getUoms } from '../../api/inventoryMaster';
import { getMaterialGroups } from '../../api/materialGroup';
import { getItemClasses } from '../../api/itemClass';
import { toSelectOptions, normalizeUomList, normalizeCategoryList, normalizeItemClassList, normalizeMaterialGroupList } from '../../utils/masterEntity';
import { exportToCsv } from '../../utils';
import { IPD_COLORS, filterToggleSx } from '../../theme';

async function loadMasterLookup(label, fetcher, onFail) {
  try {
    return await fetcher();
  } catch (err) {
    console.error(`[ItemList] ${label} lookup failed:`, err);
    onFail(label);
    return [];
  }
}

export default function ItemList() {
  const { url }      = useRouteMatch();
  const history      = useHistory();
  const location     = useLocation();

  const [items, setItems]               = useState([]);
  const [categoryById, setCategoryById] = useState({});
  const [uomById, setUomById]           = useState({});
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [lookupWarning, setLookupWarning] = useState(null);
  const [search, setSearch]             = useState('');
  const [filter, setFilter]             = useState('all');
  const [itemClass, setItemClass]           = useState('');
  const [materialGroup, setMaterialGroup]   = useState('');
  const [itemClassOptions, setItemClassOptions] = useState([]);
  const [materialGroupOptions, setMaterialGroupOptions] = useState([]);

  // Deactivate/activate dialog
  const [toggleTarget, setToggleTarget]     = useState(null); // row
  const [toggleLoading, setToggleLoading]   = useState(false);
  const [toggleError, setToggleError]       = useState('');

  // Toast for save confirmation and action feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Detect "saved" navigation state from ItemForm ────────────
  useEffect(() => {
    if (location.state?.saved) {
      setSnackbar({ open: true, message: 'Item saved successfully', severity: 'success' });
      // Clear the state so refreshing doesn't re-show the toast
      history.replace(location.pathname, {});
    }
  }, [location.state, location.pathname, history]);

  // ── Load items ───────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLookupWarning(null);
    try {
      const params = {};
      if (search) params.search = search;
      if (filter === 'pharmacy') params.is_drug = true;
      if (filter === 'non_drug') params.is_drug = false;
      if (itemClass) params.item_class_id = itemClass;
      if (materialGroup) params.material_group_id = materialGroup;

      const failedLookups = [];
      const onLookupFail = (label) => { failedLookups.push(label); };

      const [data, cats, uoms, groups, classes] = await Promise.all([
        getItems(params),
        loadMasterLookup('categories', () => getCategories({ is_active: true, limit: 200 }), onLookupFail),
        loadMasterLookup('UOMs', () => getUoms({ is_active: true, limit: 200 }), onLookupFail),
        loadMasterLookup('material groups', () => getMaterialGroups({ is_active: true, limit: 200 }), onLookupFail),
        loadMasterLookup('item classes', () => getItemClasses({ is_active: true, limit: 200 }), onLookupFail),
      ]);

      if (failedLookups.length) {
        setLookupWarning(
          `Could not load ${failedLookups.join(', ')}. Some filters may be incomplete — please refresh the page.`,
        );
      }

      setItems(Array.isArray(data) ? data : (data?.items || []));

      setItemClassOptions(toSelectOptions(normalizeItemClassList(classes), {
        idKey: 'item_class_id', nameKey: 'class_name', codeKey: 'class_code',
      }));
      setMaterialGroupOptions(toSelectOptions(normalizeMaterialGroupList(groups), {
        idKey: 'material_group_id', nameKey: 'group_name', codeKey: 'group_code',
      }));

      const catMap = {};
      for (const c of normalizeCategoryList(cats)) {
        const id = c.category_id;
        if (id) catMap[id] = c.category_name || c.name || '';
      }
      setCategoryById(catMap);

      const uMap = {};
      for (const u of normalizeUomList(uoms)) {
        const id = u.uom_id;
        if (id) {
          uMap[id] = {
            name: u.uom_name || u.name || '',
            code: u.uom_code || u.code || '',
          };
        }
      }
      setUomById(uMap);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [search, filter, itemClass, materialGroup]);

  useEffect(() => { load(); }, [load]);

  // Client-side filter — inventory tab excludes MEDICINE until backend exclude_item_type exists
  let filtered = items;
  if (filter === 'inventory') {
    filtered = items.filter(i => i.item_type !== 'MEDICINE');
  }
  if (itemClass) {
    filtered = filtered.filter(i =>
      String(i.item_class_id || i.item_class?.item_class_id || i.item_class?.id || '') === String(itemClass),
    );
  }
  if (materialGroup) {
    filtered = filtered.filter(i =>
      String(i.material_group_id || i.material_group?.material_group_id || i.material_group?.id || '') === String(materialGroup),
    );
  }

  // ── Activate / deactivate ────────────────────────────────────
  async function handleToggleActive() {
    if (!toggleTarget) return;
    setToggleLoading(true);
    setToggleError('');
    const newActive = !toggleTarget.is_active;
    try {
      // API: PATCH /inventory/items/{id}/
      await updateItem(toggleTarget.item_id || toggleTarget.id, { is_active: newActive });
      setToggleTarget(null);
      setSnackbar({
        open: true,
        message: `Item ${newActive ? 'activated' : 'deactivated'} successfully`,
        severity: 'success',
      });
      load();
    } catch (err) {
      setToggleError(err.message || 'Failed to update item status');
      setSnackbar({ open: true, message: err.message || 'Failed to update item', severity: 'error' });
    } finally {
      setToggleLoading(false);
    }
  }

  function resolveCategoryName(row) {
    return (
      row.category?.category_name
      || row.category_name
      || categoryById[row.category_id]
      || '—'
    );
  }

  function resolveUomLabel(row) {
    const nested = row.base_uom;
    if (nested?.uom_name) {
      return nested.uom_code ? `${nested.uom_name} (${nested.uom_code})` : nested.uom_name;
    }
    if (row.base_uom_name) return row.base_uom_name;

    const fromMaster = uomById[row.base_uom_id];
    if (fromMaster?.name) {
      return fromMaster.code ? `${fromMaster.name} (${fromMaster.code})` : fromMaster.name;
    }
    return '—';
  }

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => [
    { field: 'item_code', headerName: 'Code', width: 110 },
    {
      field: 'item_name', headerName: 'Name', flex: 1, minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {params.value}
          <ControlledDrugBadge show={!!params.row.is_controlled_drug} />
        </Box>
      ),
    },
    {
      field: 'category_name', headerName: 'Category', width: 160,
      valueGetter: (params) => resolveCategoryName(params.row),
    },
    {
      field: 'base_uom_name', headerName: 'UOM', width: 110,
      valueGetter: (params) => resolveUomLabel(params.row),
    },
    {
      field: 'item_type', headerName: 'Module', width: 120,
      renderCell: (params) => <ModuleBadge itemType={params.value} />,
    },
    {
      field: 'is_drug', headerName: 'Drug', width: 70,
      align: 'center', headerAlign: 'center',
      renderCell: (params) => params.value
        ? <CheckCircleOutlineIcon sx={{ color: IPD_COLORS.primary, fontSize: 18 }} />
        : <span style={{ color: IPD_COLORS.textSecondary }}>—</span>,
    },
    {
      field: 'primary_barcode', headerName: 'Barcode', width: 130,
      valueGetter: (params) => params.row.primary_barcode || '—',
    },
    {
      field: 'weighted_avg_cost', headerName: 'WAC', width: 90,
      align: 'right', headerAlign: 'right',
      renderCell: (params) => params.value
        ? <span>₹{Number(params.value).toFixed(2)}</span>
        : <span style={{ color: IPD_COLORS.textSecondary }}>—</span>,
    },
    {
      field: 'is_active', headerName: 'Active', width: 80,
      align: 'center', headerAlign: 'center',
      renderCell: (params) => params.value
        ? <CheckCircleOutlineIcon sx={{ color: IPD_COLORS.success.text, fontSize: 18 }} />
        : <CancelIcon sx={{ color: IPD_COLORS.error.text, fontSize: 18 }} />,
    },
    {
      field: 'is_batch_tracked', headerName: 'Batch', width: 75,
      align: 'center', headerAlign: 'center',
      renderCell: (params) => params.value
        ? <CheckCircleOutlineIcon sx={{ color: IPD_COLORS.primary, fontSize: 18 }} />
        : <span style={{ color: IPD_COLORS.textSecondary }}>—</span>,
    },
    {
      field: 'is_expiry_tracked', headerName: 'Expiry', width: 75,
      align: 'center', headerAlign: 'center',
      renderCell: (params) => params.value
        ? <CheckCircleOutlineIcon sx={{ color: IPD_COLORS.primary, fontSize: 18 }} />
        : <span style={{ color: IPD_COLORS.textSecondary }}>—</span>,
    },
    {
      field: 'actions', headerName: '', width: 120, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => history.push(`${url}/${params.row.item_id || params.row.id}/edit`)}
              sx={{ color: IPD_COLORS.primary }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Stock">
            <IconButton
              size="small"
              onClick={() => history.push(`${url.replace('/material/items', '/stock/balances')}?item_id=${params.row.item_id || params.row.id}`)}
              sx={{ color: IPD_COLORS.textSecondary }}
            >
              <ShowChartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.is_active ? 'Deactivate item' : 'Activate item'}>
            <IconButton
              size="small"
              onClick={() => setToggleTarget(params.row)}
              sx={{ color: params.row.is_active ? IPD_COLORS.textSecondary : IPD_COLORS.success.text }}
            >
              <PowerSettingsNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [categoryById, uomById, history, url]);

  const hasActiveFilters = Boolean(search.trim() || filter !== 'all' || itemClass || materialGroup);

  function handleResetFilters() {
    setSearch('');
    setFilter('all');
    setItemClass('');
    setMaterialGroup('');
  }

  return (
    <Box>
      <PageHeader
        title="Item Master"
        badge={`${filtered.length} items`}
        actions={
          <>
            <InvButton
              startIcon={<DownloadIcon />} variant="outlined" tone="teal" size="small"
              onClick={() => exportToCsv('items.csv', filtered)}
            >
              Export
            </InvButton>
            <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${url}/new`)}>
              New Item
            </InvButton>
          </>
        }
      />


      {/* ── Error banner ──────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>Retry</Button>
          }
        >
          Failed to load items.
        </Alert>
      )}

      {lookupWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setLookupWarning(null)}>
          {lookupWarning}
        </Alert>
      )}

      <FilterToolbar>
        <FilterToolbar.Search
          placeholder="Search by name or code..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <FilterToolbar.Slot>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, v) => v && setFilter(v)}
            size="small"
            sx={{ ...filterToggleSx, height: 40 }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="pharmacy">Pharmacy (Drug)</ToggleButton>
            <ToggleButton value="non_drug">Non-drug</ToggleButton>
            <ToggleButton value="inventory">Inventory</ToggleButton>
          </ToggleButtonGroup>
        </FilterToolbar.Slot>
        <FilterToolbar.Filter
          label="Material Group"
          width="wide"
          value={materialGroup}
          onChange={e => setMaterialGroup(e.target.value)}
          options={[{ value: '', label: 'All groups' }, ...materialGroupOptions]}
        />
        <FilterToolbar.Filter
          label="Item Class"
          width="wide"
          value={itemClass}
          onChange={e => setItemClass(e.target.value)}
          options={[{ value: '', label: 'All classes' }, ...itemClassOptions]}
        />
        <FilterToolbar.Reset onClick={handleResetFilters} disabled={!hasActiveFilters} />
      </FilterToolbar>

      <InvDataGrid
        fillHeight
        rows={filtered}
        columns={columns}
        loading={loading}
        getRowId={row => row.item_id || row.id}
        emptyMessage="No items found. Create your first item."
        emptyAction={
          <InvButton onClick={() => history.push(`${url}/new`)} startIcon={<AddIcon />}>
            Create Item
          </InvButton>
        }
      />

      {/* ── Deactivate / Activate confirm dialog ─────────────── */}
      <ConfirmDialog
        open={Boolean(toggleTarget)}
        title={toggleTarget?.is_active
          ? `Deactivate ${toggleTarget?.item_name}?`
          : `Activate ${toggleTarget?.item_name}?`}
        description={toggleTarget?.is_active
          ? 'This item will no longer appear in new transactions. Existing records are not affected.'
          : 'This item will be available again in new transactions.'}
        destructive={toggleTarget?.is_active}
        loading={toggleLoading}
        onConfirm={handleToggleActive}
        onCancel={() => { setToggleTarget(null); setToggleError(''); }}
      />

      {/* ── Toast ─────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
